import { apiClient } from '@/lib/api-client';

/**
 * WhatsApp inbox API for the floating chat widget. Server contract:
 * `apps/api/src/modules/doubletick/whatsapp-inbox.controller.ts`
 * (docs/integrations/doubletick-whatsapp.md). Passthrough types - the
 * payload shapes are still settling while media/status events get captured,
 * so no shared Zod schema yet.
 */

/** One phone in a per-lead WhatsApp group: invited by us and/or joined. */
export interface WhatsAppGroupMember {
  phone: string;
  name: string | null;
  role: 'CUSTOMER' | 'OPS' | 'TEAM' | 'OTHER';
  invitedAt: string | null;
  joinedAt: string | null;
  leftAt: string | null;
}

/** The DoubleTick WhatsApp group behind a GROUP conversation. */
export interface WhatsAppGroupInfo {
  groupId: string | null;
  /** WhatsApp invite link - the only way into the group; staff-only. */
  inviteLink: string | null;
  members: WhatsAppGroupMember[];
}

export interface WhatsAppInboxItem {
  id: string;
  phone: string;
  /** DIRECT: one customer number. GROUP: the lead's WhatsApp group (ops + customer). */
  kind: 'DIRECT' | 'GROUP';
  group: WhatsAppGroupInfo | null;
  contactName: string | null;
  unreadCount: number;
  lastMessageAt: string | null;
  windowOpen: boolean;
  /** The last send bounced as undeliverable - the number is not on WhatsApp. */
  notOnWhatsApp: boolean;
  lastMessage: { direction: string; kind: string; body: string | null; status: string } | null;
  lead: {
    id: string;
    reference: string;
    customerName: string;
    destination: string | null;
    /** Seller the lead is assigned to - drives the inbox's "My chats". */
    assignedAgentId: string | null;
    /**
     * That seller's name - sent only to ADMIN / SALES_LEAD (absent for every
     * other role). Null = the lead is unassigned.
     */
    assignedAgentName?: string | null;
  } | null;
}

export interface WhatsAppInbox {
  totalUnread: number;
  conversations: WhatsAppInboxItem[];
}

/**
 * The parts of a template message around its body, as the customer received
 * them. A dynamic URL button sent before this was stored has no `url`.
 */
export interface WhatsAppTemplateView {
  /** TEXT | IMAGE | VIDEO | DOCUMENT, null when the template has no header. */
  headerFormat: string | null;
  headerText: string | null;
  footer: string | null;
  buttons: Array<{
    /** URL | QUICK_REPLY | PHONE_NUMBER | ... */
    type: string;
    text: string;
    url?: string | null;
    phoneNumber?: string | null;
  }>;
}

export interface WhatsAppThreadMessage {
  id: string;
  direction: 'IN' | 'OUT';
  kind: string;
  body: string | null;
  mediaUrl: string | null;
  templateName: string | null;
  templateView?: WhatsAppTemplateView | null;
  status: string;
  errorMessage: string | null;
  /** Group chats: who sent an IN message (E.164 digits) and their WhatsApp name. */
  senderPhone?: string | null;
  senderName?: string | null;
  sentAt: string | null;
  createdAt: string;
}

export interface WhatsAppThread {
  conversation: {
    id: string;
    phone: string;
    contactName: string | null;
    lastInboundAt: string | null;
  };
  kind?: 'DIRECT' | 'GROUP';
  group?: WhatsAppGroupInfo | null;
  messages: WhatsAppThreadMessage[];
  lead: {
    id: string;
    reference: string;
    customerName: string;
    destination: string | null;
    /** Lead owner's name - ADMIN / SALES_LEAD only (absent otherwise). */
    assignedAgentName?: string | null;
  } | null;
  windowOpen: boolean;
  windowExpiresAt: string | null;
  /** See WhatsAppInboxItem.notOnWhatsApp. */
  notOnWhatsApp?: boolean;
  callPermission?: WhatsAppCallPermission | null;
}

/**
 * The customer's latest answer to a call-permission request. Null = nothing
 * on record: never asked, never answered, or answered before these were kept.
 */
export interface WhatsAppCallPermission {
  response: 'accept' | 'reject';
  /** "Always allow", not the temporary grant. */
  isPermanent: boolean;
  /** When a temporary grant lapses. */
  expiresAt: string | null;
  at: string;
}

export const listWhatsAppConversations = async (): Promise<WhatsAppInbox> => {
  const { data } = await apiClient.get<WhatsAppInbox>('/whatsapp/conversations');
  return data;
};

export interface WhatsAppTemplateDef {
  name: string;
  label: string;
  params: string[];
  /** Labels for dynamic URL-button suffixes (e.g. quote code), if any. */
  buttonParams?: string[];
  /** A call-permission request - not offered once the customer allowed calls for good. */
  callPermission?: boolean;
}

export interface ManagedWhatsAppTemplate extends WhatsAppTemplateDef {
  hidden: boolean;
}

/** ADMIN: every approved template with its picker-visibility flag. */
export const listWhatsAppTemplatesManage = async (): Promise<{
  templates: ManagedWhatsAppTemplate[];
}> => {
  const { data } = await apiClient.get<{ templates: ManagedWhatsAppTemplate[] }>(
    '/whatsapp/templates/manage',
  );
  return data;
};

/** ADMIN: toggle one template's visibility in the chat picker. */
export const setWhatsAppTemplateHidden = async (name: string, hidden: boolean): Promise<void> => {
  await apiClient.patch('/whatsapp/templates/manage', { name, hidden });
};

/** Only templates currently APPROVED on the DoubleTick account. */
export const listWhatsAppTemplates = async (): Promise<{ templates: WhatsAppTemplateDef[] }> => {
  const { data } = await apiClient.get<{ templates: WhatsAppTemplateDef[] }>('/whatsapp/templates');
  return data;
};

export const getWhatsAppThread = async (conversationId: string): Promise<WhatsAppThread> => {
  const { data } = await apiClient.get<WhatsAppThread>(`/whatsapp/conversations/${conversationId}`);
  return data;
};

export type WhatsAppAttachmentKind = 'IMAGE' | 'VIDEO' | 'AUDIO' | 'DOCUMENT';

export interface SendWhatsAppPayload {
  text?: string;
  templateName?: string;
  templateParams?: string[];
  buttonParams?: string[];
  attachment?: {
    s3Key: string;
    kind: WhatsAppAttachmentKind;
    filename?: string;
    caption?: string;
  };
  location?: { latitude: number; longitude: number; name?: string; address?: string };
}

export const sendWhatsAppMessage = async (
  conversationId: string,
  payload: SendWhatsAppPayload,
): Promise<WhatsAppThreadMessage> => {
  const { data } = await apiClient.post<WhatsAppThreadMessage>(
    `/whatsapp/conversations/${conversationId}/messages`,
    payload,
  );
  return data;
};

export interface WhatsAppAttachmentPresign {
  s3Key: string;
  kind: WhatsAppAttachmentKind;
  presignedUrl: string;
  expiresAt: string;
}

/**
 * Two-step attachment upload: presign, PUT the file straight to S3 (the API
 * never sees the binary), then include `{s3Key, kind}` on the send payload.
 */
export const presignWhatsAppAttachment = async (file: File): Promise<WhatsAppAttachmentPresign> => {
  const { data } = await apiClient.post<WhatsAppAttachmentPresign>(
    '/whatsapp/attachments/presign',
    {
      filename: file.name,
      mimeType: file.type || 'application/octet-stream',
      sizeBytes: file.size,
    },
  );
  return data;
};

export const uploadWhatsAppAttachment = async (file: File): Promise<WhatsAppAttachmentPresign> => {
  const presign = await presignWhatsAppAttachment(file);
  const put = await fetch(presign.presignedUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
    body: file,
  });
  if (!put.ok) throw new Error(`Upload failed (${put.status}). Try again.`);
  return presign;
};

/**
 * Outbound media messages store `s3://<key>` as mediaUrl; swap it for a
 * short-lived presigned GET when the agent clicks. Call on demand - the
 * URL expires in 5 minutes.
 */
export const getWhatsAppAttachmentViewUrl = async (s3Url: string): Promise<string> => {
  const key = s3Url.replace(/^s3:\/\//, '');
  const { data } = await apiClient.get<{ url: string }>('/whatsapp/attachments/view-url', {
    params: { key },
  });
  return data.url;
};

/**
 * Inbound (customer-sent) media lives on DoubleTick's storage and 403s
 * without the account API key, which never reaches the browser - so fetch
 * the bytes through the API's media proxy and hand back an object URL.
 */
export const isDoubleTickMediaUrl = (url: string): boolean => {
  try {
    const host = new URL(url).hostname;
    return host === 'doubletick.io' || host.endsWith('.doubletick.io');
  } catch {
    return false;
  }
};

export const fetchInboundMediaObjectUrl = async (url: string): Promise<string> => {
  const { data } = await apiClient.get<Blob>('/whatsapp/media-proxy', {
    params: { url },
    responseType: 'blob',
  });
  return URL.createObjectURL(data);
};
